import dayjs from 'dayjs';
import { forwardRef, useContext, useEffect, useState } from 'react';
import { Button, FormSelect } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faCirclePlus,
  faCircleXmark,
  faMoon,
  faSun,
} from '@fortawesome/free-solid-svg-icons';
import { WidgetContainer, WidgetContainerContext } from '@/core/widgets/export';
import { authService, useSessionId } from '@/core/auth/export';
import {
  analyticsService,
  AnalyticsEvent,
  AnalyticsSubTypeEvent,
  AnalyticsTypes,
} from '@/shared/analytics/export';
import ReactDatePicker from 'react-datepicker';

interface Timezone {
  code: string;
  name: string;
}

interface ClockPreferences {
  selectedTimezones: string[];
}

const LocalZone = { code: 'local', name: 'Local' };

export default function Clock() {
  const widgetContainer = useContext<WidgetContainer>(WidgetContainerContext);
  const [time, setTime] = useState<number>(Date.now());
  const [nominalCustomDate, setNominalCustomDate] = useState<string>();
  const [customDate, setCustomDate] = useState<number | undefined>();
  const [customDateZone, setCustomDateZone] = useState<Timezone>(LocalZone);
  const [selected, setSelected] = useState<string[]>(
    widgetContainer.getPreferences<ClockPreferences>()?.selectedTimezones || [],
  );

  const sessionId = useSessionId();
  const subject = authService.getSubjectData();
  const [selectedOption, setSelectedOption] = useState<string>();

  const timezones: Timezone[] = [
    { code: 'America/New_York', name: 'New York' },
    { code: 'America/Chicago', name: 'Chicago' },
    { code: 'Europe/London', name: 'London' },
    { code: 'Europe/Berlin', name: 'Berlin' },
    { code: 'Asia/Shanghai', name: 'Beijing' },
    { code: 'Asia/Tokyo', name: 'Tokyo' },
    { code: 'America/Los_Angeles', name: 'Los Angeles' },
    { code: 'America/Sao_Paulo', name: 'Sao Paulo' },
    { code: 'Australia/Sydney', name: 'Sydney' },
    { code: 'Asia/Singapore', name: 'Singapore' },
    { code: 'Asia/Dubai', name: 'Dubai' },
    LocalZone,
    { code: 'America/Argentina/Buenos_Aires', name: 'Buenos Aires' },
  ];

  const remainingOptions = timezones.filter(
    value => value.code !== 'local' && !selected.includes(value.code),
  );

  useEffect(() => {
    setSelectedOption(undefined);

    widgetContainer.setPreferences<ClockPreferences>({
      selectedTimezones: selected,
    });
    // eslint-disable-next-line
  }, [selected]);

  useEffect(() => {
    const interval = setInterval(() => setTime(Date.now()), 10_000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!nominalCustomDate) setCustomDate(undefined);
    else if (customDateZone.code !== 'local')
      return setCustomDate(dayjs.tz(nominalCustomDate, customDateZone.code).valueOf());
    else return setCustomDate(dayjs(nominalCustomDate).valueOf());
  }, [nominalCustomDate, customDateZone]);

  const effectiveTime = () => {
    if (customDate !== undefined) return customDate;
    return time;
  };

  const TimeZoneDisplay = ({ code }: { code: string }) => {
    // eslint-disable-next-line
    const value = timezones.find(tz => tz.code == code);

    if (!value) return <></>;

    const date =
      value.code === 'local' ? dayjs(effectiveTime()) : dayjs(effectiveTime()).tz(value.code);

    let night = false;

    if (date.get('hours') > 17 || date.get('hours') < 8) night = true;

    return (
      <div>
        <div
          key={value.code}
          className={`border-bottom ${value.code === 'local' ? 'fw-bold' : 'fw-light'}`}
        >
          <div className={`d-flex align-items-center ${night && 'text-info-emphasis '}`}>
            <span style={{ width: '1em' }}>
              {night ? (
                <div className={'text-info'}>
                  <FontAwesomeIcon icon={faMoon} />
                </div>
              ) : (
                <div className={'text-warning-emphasis'}>
                  <FontAwesomeIcon icon={faSun} />
                </div>
              )}
            </span>

            <div className={'ms-2'}> {value.name} </div>

            <div className={' ms-auto font-monospace  d-flex'}>
              <span className={customDate ? 'text-warning' : ''}>{date.format('h:mm a')}</span>
            </div>
            <span className={'d-flex'}>
              <Button
                variant={''}
                size={'sm'}
                className={'link-danger'}
                onClick={() => {
                  // eslint-disable-next-line
                  const remaining = selected.filter(value => value != code);
                  setSelected(remaining);
                  analyticsService.recordAnalyticsEvent(AnalyticsEvent.WIDGET_CONFIG_CHANGED, {
                    subject,
                    object: {
                      eventSubType: AnalyticsSubTypeEvent.WORLD_CLOCK_LOCATION_CHANGED,
                      type: AnalyticsTypes.WIDGET,
                      id: widgetContainer.widgetId,
                      config: remaining.join(','),
                    },
                    session: {
                      id: sessionId,
                    },
                  });
                }}
              >
                <FontAwesomeIcon
                  icon={faCircleXmark}
                  className={value.code === 'local' ? 'opacity-0' : ''}
                />
              </Button>{' '}
            </span>
          </div>
          <div className={'d-flex justify-content-between'}>
            <span></span>
          </div>
        </div>
      </div>
    );
  };
  const DatePickerButton = forwardRef<any, any>(({ onClick }, ref) => (
    <button
      className={`btn btn-sm btn-outline-secondary btn-menu date-picker me-1`}
      onClick={onClick}
      ref={ref}
    >
      <span className={''}>
        {nominalCustomDate ? (
          dayjs(nominalCustomDate).format('h:mm a')
        ) : (
          <FontAwesomeIcon icon={faCalendar} />
        )}
      </span>
    </button>
  ));

  return (
    <div className={'container-element h-100  overflow-auto'}>
      <div className={'w-100 d-flex container-responsive-text'}>
        <ReactDatePicker
          onChange={(date: any) => {
            if (date instanceof Date) {
              setNominalCustomDate(dayjs(date).format('YYYY-MM-DD HH:mm:ss'));
            }
          }}
          showTimeSelect={true}
          customInput={<DatePickerButton />}
        />

        <FormSelect
          size={'sm'}
          className={'w-25'}
          onChange={event =>
            setCustomDateZone(
              timezones.find(value => value.code === event.target.value) || LocalZone,
            )
          }
        >
          {timezones.map(value => (
            <option
              key={value.code}
              value={value.code}
              selected={value.code === customDateZone.code}
            >
              {value.name}
            </option>
          ))}
        </FormSelect>
        {customDate && (
          <Button
            size={'sm'}
            className={'btn-outline-warning ms-auto'}
            onClick={() => setNominalCustomDate(undefined)}
          >
            Show Current Time
          </Button>
        )}
      </div>

      <div className={'container-responsive-text  pt-2'}>
        <TimeZoneDisplay code={'local'} />
        {selected.map(value => (
          <TimeZoneDisplay code={value} />
        ))}
        {remainingOptions.length !== 0 && (
          <div className={'mt-2'}>
            <div className={'input-group'}>
              <select
                className={'form-select form-select-sm'}
                onChange={event => setSelectedOption(event.target.value)}
              >
                <option>-- add new --</option>
                {remainingOptions.map(value => (
                  <option key={'o-' + value.code} value={value.code}>
                    {value.name}
                  </option>
                ))}
              </select>
              <Button
                variant={'secondary'}
                size={'sm'}
                disabled={!selectedOption}
                onClick={() => {
                  if (selectedOption) {
                    const worldClocks = [...selected, selectedOption];
                    setSelected(worldClocks);

                    analyticsService.recordAnalyticsEvent(AnalyticsEvent.WIDGET_CONFIG_CHANGED, {
                      subject,
                      object: {
                        eventSubType: AnalyticsSubTypeEvent.WORLD_CLOCK_LOCATION_CHANGED,
                        type: AnalyticsTypes.WIDGET,
                        id: widgetContainer.widgetId,
                        config: worldClocks.join(','),
                      },
                      session: {
                        id: sessionId,
                      },
                    });
                  }
                }}
              >
                <FontAwesomeIcon icon={faCirclePlus} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
